import { Icon36CoinsStacks2Outline, Icon36Like } from "@vkontakte/icons";
import { useRouteNavigator } from "@vkontakte/vk-mini-apps-router";
import { Spinner, Tooltip } from "@vkontakte/vkui";
import React, { useEffect, useState } from 'react';
import Countdown from "../../components/Countdown/Countdown";
import { add3DailyAttempts, getDailyAttemptsPrice, getPlayerInfo } from "../../services/player";
import { getActiveGame } from "../../services/game";
import BonusStore from "../../store/bonus";
import GameStore from "../../store/game";
import { numberWordSuffix } from "../../utils/word";
import { Add3DailyAttemptsBonusPrice } from "../../model/prices";
import { useSnackbar } from "../../context/SnackbarContext";
import { observer } from "mobx-react-lite";
import { BUY_COINS_MODAL } from "../../routing/routing";
import {catalogPreviewHomePlayer, isCatalogPreview} from "../../utils/catalogPreview";

// StartGame - кнопка, которая позволяет начать игру на главной странице
const StartButton = observer(({ handleButton }) => {
    const catalogPreview = isCatalogPreview();
    const routeNavigator = useRouteNavigator();
    const [player, setPlayer] = useState(catalogPreview ? catalogPreviewHomePlayer : {});
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(!catalogPreview);
    const [isActiveGameLoading, setIsActiveGameLoading] = useState(!catalogPreview);
    // Активная игра существует, если есть валидный идентификатор и флаг активности
    const hasRunningGame = GameStore.isActive && Number.isFinite(Number(GameStore.gameID)) && Number(GameStore.gameID) > 0;
    const [dailyAttemptPrice, setDailyAttemptPrice] = useState(Add3DailyAttemptsBonusPrice);
    const availableAttempts = player.available_daily_attempts ?? player.daily_attempts ?? 0;
    const progression = player.progression;
    const levelProgress = progression
        ? Math.min(100, Math.max(0, Math.round(((progression.totalScore - progression.currentLevelScore) / Math.max(progression.nextLevelScore - progression.currentLevelScore, 1)) * 100)))
        : 0;
    const showPriceResetDisclaimer = dailyAttemptPrice > Add3DailyAttemptsBonusPrice;
    const fetchPlayerInfo = () => {
        return getPlayerInfo().then(resp => {
            const player = resp.data.response;
            setPlayer(player);
            BonusStore.setCoins(player.coins);
            const totalDailyAttempts = player.available_daily_attempts ?? player.daily_attempts ?? 0;
            GameStore.setDailyAttempts(totalDailyAttempts);
        }).catch(err => {
            console.error("Не удалось получить информацию об игроке: ", err);
        });
    };

    const fetchDailyAttemptPrice = () => {
        getDailyAttemptsPrice().then(resp => {
            const price = resp?.data?.response?.price;
            if (Number.isFinite(price)) {
                setDailyAttemptPrice(price);
            }
        }).catch(err => {
            console.error("Не удалось получить цену покупки жизней:", err);
            setDailyAttemptPrice(Add3DailyAttemptsBonusPrice);
        });
    };

    // Запрашивает активную игру и синхронизирует статус кнопки
    const fetchActiveGame = () => {
        setIsActiveGameLoading(true);
        getActiveGame().then(resp => {
            const data = resp?.data?.response ?? resp?.data;
            if (!data) {
                GameStore.setIsActive(false);
                GameStore.setGameID("");
                return;
            }

            if (data.isActive) {
                const gameId = data.game?.gameId ?? data.game?.id;
                if (Number.isFinite(Number(gameId)) && Number(gameId) > 0) {
                    GameStore.setGameID(gameId);
                }
                GameStore.setIsActive(true);
                return;
            }

            GameStore.setIsActive(false);
            GameStore.setGameID("");
        }).catch(err => {
            console.error("Не удалось получить активную игру:", err);
        }).finally(() => {
            setIsActiveGameLoading(false);
        });
    };

    useEffect(() => {
        // Режим каталога не запрашивает профиль и оставляет заполненный стартовый экран.
        if (catalogPreview) {
            BonusStore.setCoins(catalogPreviewHomePlayer.coins);
            GameStore.setDailyAttempts(catalogPreviewHomePlayer.available_daily_attempts);
            GameStore.setIsActive(false);
            GameStore.setGameID("");
            return;
        }
        // Получаем данные игрока
        fetchPlayerInfo().finally(() => {
            setIsLoading(false);
        });

        fetchDailyAttemptPrice();
        fetchActiveGame();
    }, []);

    // Обрабатывает нажатие на кнопку "Играть" за монеты
    const playButtonHandler = () => {
        if (handleButton) {
            handleButton();
        }

        // Чистим состояние игры только при старте новой сессии
        if (!hasRunningGame) {
            GameStore.flush();
        }

        // Переходим на страницу с игрой
        routeNavigator.push('/game');
    };

    // Обрабатывает нажатие на кнопку "Купить жизни" за монеты
    const addNewDailyAttemptsHandler = () => {
        if (BonusStore.coins < dailyAttemptPrice) {
            showErrorSnackbar("Не хватает монет");
            routeNavigator.showModal(BUY_COINS_MODAL);
            return;
        }

        add3DailyAttempts().then(resp => {
            const player = resp.data.response;
            setPlayer(player);
            BonusStore.setCoins(player.coins);
            const totalDailyAttempts = player.available_daily_attempts ?? player.daily_attempts ?? 0;
            GameStore.setDailyAttempts(totalDailyAttempts);
            fetchDailyAttemptPrice();

            showSuccessSnackbar("Жизни восстановлены");
        });
    };

    const isInitialLoading = isLoading || isActiveGameLoading;
    const isPlayButtonDisabled = isInitialLoading;
    const playButtonDescription = hasRunningGame
        ? "Продолжайте раунд и заберите ещё больше очков."
        : "Запустите новый раунд и соберите новый рекорд.";
    const playButtonClassName = isPlayButtonDisabled
        ? "arcade-play-button cursor-default opacity-60"
        : "arcade-play-button";

    return (<div className={"flex w-full flex-col"}>
        <div className="arcade-main-menu w-full">
            {/* Плейсхолдеры сохраняют высоту блока и убирают мерцание во время загрузки */}
            {isLoading && (
                <div className={"arcade-menu-content"}>
                    <div className={"animate-pulse flex flex-col items-center gap-3"}>
                        <div className={"h-10 w-full rounded-ui bg-ui-muted/70"}></div>
                        <div className={"h-[92px] w-[92px] rounded-[24px] bg-ui-muted/50"}></div>
                        <div className={"h-12 w-full max-w-sm rounded-ui bg-ui-muted/40"}></div>
                    </div>
                </div>
            )}

            {!isLoading && <>
                {(hasRunningGame || availableAttempts > 0) &&
                    <div className="arcade-menu-content">
                        <div className="flex w-full items-center justify-between gap-2">
                            <span className="arcade-title text-lg font-black">Новая игра</span>
                            {isInitialLoading && <Spinner size={"small"} />}
                        </div>

                        <div className={"flex flex-col items-center gap-3"}>
                            <button
                                type="button"
                                aria-label={hasRunningGame ? "Продолжить игру" : "Начать новую игру"}
                                className={playButtonClassName}
                                disabled={isPlayButtonDisabled}
                                onClick={isPlayButtonDisabled ? undefined : playButtonHandler}>
                                <span className="arcade-play-button__label">
                                    {hasRunningGame ? "Продолжить игру" : "Начать игру"}
                                </span>
                            </button>
                        </div>

                        <div className={"text-center text-sm font-semibold text-gray-900"}>
                            {playButtonDescription}
                        </div>

                        {progression && <ProgressionPanel progression={progression} progress={levelProgress} />}

                        <BalancePanel player={player} availableAttempts={availableAttempts} />
                    </div>
                }

                {(!hasRunningGame && availableAttempts === 0) &&
                    <div className="arcade-menu-content">
                        <div className="flex w-full items-center justify-between gap-2">
                            <span className="arcade-title text-lg font-black">Новая игра</span>
                            {isInitialLoading && <Spinner size={"small"} />}
                        </div>
                        <Countdown variant="light" onComplete={fetchPlayerInfo} />

                        <div className={"flex justify-center items-center flex-col gap-1"}>
                            <div
                                className={BonusStore.coins < dailyAttemptPrice ? "arcade-buy-life-button cursor-default opacity-60" : "arcade-buy-life-button cursor-pointer"}
                                onClick={addNewDailyAttemptsHandler}>
                                <div className="text-sm">Пополнить жизни</div>
                                <div className="text-xs">
                                    3 <span className="leading-none">❤️</span> за {dailyAttemptPrice} {numberWordSuffix(dailyAttemptPrice, ["монету", "монеты", "монет"])}
                                </div>
                            </div>
                        </div>
                        {showPriceResetDisclaimer &&
                            <div className={"mt-2 text-xs text-gray-700 text-center"}>
                                Завтра стоимость снова упадёт до {Add3DailyAttemptsBonusPrice} монет.
                            </div>
                        }

                        <BalancePanel player={player} availableAttempts={availableAttempts} />

                    </div>
                }
            </>}
        </div>
    </div>);
})

export default StartButton;

// ProgressionPanel отображает уровень, звание и прогресс игрока.
const ProgressionPanel = ({ progression, progress }) => {
    const [isProgressionHelpOpen, setIsProgressionHelpOpen] = useState(false);

    useEffect(() => {
        if (!isProgressionHelpOpen) {
            return;
        }

        // Автоматически скрывает подсказку о прогрессии.
        const timer = setTimeout(() => {
            setIsProgressionHelpOpen(false);
        }, 7000);

        return () => {
            clearTimeout(timer);
        };
    }, [isProgressionHelpOpen]);

    useEffect(() => {
        if (!isProgressionHelpOpen) {
            return;
        }

        // Закрывает подсказку по первому следующему нажатию на экран.
        const handleScreenPointerDown = () => {
            setIsProgressionHelpOpen(false);
        };

        document.addEventListener("pointerdown", handleScreenPointerDown);

        return () => {
            document.removeEventListener("pointerdown", handleScreenPointerDown);
        };
    }, [isProgressionHelpOpen]);

    return (
        <div className="arcade-card w-full px-3 py-2">
            <div className="flex items-center justify-between gap-2">
                <div>
                    <div className="text-xs text-gray-700">Уровень {progression.level}</div>
                    <div className="text-sm font-bold text-gray-900">
                        {progression.title}
                    </div>
                </div>
                <div className="flex items-center justify-end gap-1 text-xs text-gray-700">
                    <span>{progression.totalScore}/{progression.nextLevelScore}</span>
                    <Tooltip
                        text="Уровень растёт от суммы очков за завершённые игры. Полоса показывает путь до следующего уровня. Звания: 1–4 — Новичок; 5–9 — Словоискатель; 10–14 — Знаток; 15–19 — Эрудит; 20+ — Мастер слов."
                        isShown={isProgressionHelpOpen}
                        onClose={() => setIsProgressionHelpOpen(false)}
                        className="z-50"
                        alignY="bottom"
                        alignX="right"
                        offsetY={8}
                    >
                        <button
                            type="button"
                            className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-ui-info bg-ui-info/20 text-[10px] font-bold text-ui-info hover:text-gray-900 hover:border-ui-focus"
                            onClick={() => setIsProgressionHelpOpen(!isProgressionHelpOpen)}
                            aria-label="Как рассчитывается уровень"
                            aria-expanded={isProgressionHelpOpen}
                        >
                            ?
                        </button>
                    </Tooltip>
                </div>
            </div>
            <div className="arcade-progress mt-2 h-2.5">
                <div className="arcade-progress__bar" style={{ width: `${progress}%` }} />
            </div>
        </div>
    );
};

const BalancePanel = observer(({ player, padding, availableAttempts }) => {
    const routeNavigator = useRouteNavigator();

    const handleBuyCoinsClick = () => {
        routeNavigator.showModal(BUY_COINS_MODAL);
    };

    const lives = availableAttempts ?? player.available_daily_attempts ?? player.daily_attempts ?? 0;

    return <>
        <div className={"w-full text-gray-800 " + padding}>
            {/* Основная панель с ресурсами игрока */}
            <div className={"arcade-resource-bar"}>
                <div className={"arcade-resource-pill"}>
                    <Icon36CoinsStacks2Outline className={"text-yellow-500 drop-shadow"} height={18} width={18} />
                    <span>{BonusStore.coins}</span>
                    <button
                        type="button"
                        className={"arcade-resource-buy"}
                        onClick={handleBuyCoinsClick}
                        aria-label="Купить монеты"
                    >
                        <span>Купить монеты</span>
                    </button>
                </div>

                <div className={"arcade-resource-pill"}>
                    <Icon36Like className={"text-brand-danger"} height={18} width={18} />
                    <span>{lives}</span>
                    <span className={"text-xs font-bold text-gray-700"}>{numberWordSuffix(lives, ["жизнь", "жизни", "жизней"])}</span>
                </div>
            </div>
        </div>
    </>;
});
